import { useState, useEffect, useMemo } from "react";
import type { FormEvent } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Minus, ChevronDown, ChevronUp } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { usePage } from "@/contexts/PageContext";
import type { ShippingAddress, PaymentRequest } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { 
  Elements, 
  AddressElement,
  useStripe,
  useElements,
  CardElement,
  PaymentRequestButtonElement,
} from '@stripe/react-stripe-js';

interface Product {
  _id: string;
  name?: string;
  price: number;
}


const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#424770",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#9e2146",
    },
  },
};

interface CheckoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CheckoutStepProps {
  cartItems: Array<{ product: Product; quantity: number }>;
  total: number;
  name: string;
  email: string;
  onPaymentMethod: (paymentMethodId: string, tipAmount?: number) => Promise<void>;
  isProcessing: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  setIsProcessing: (isProcessing: boolean) => void;
  checkoutSettings: {
    tipping?: {
      enabled: boolean;
      amount1?: number;
      amount2?: number;
      amount3?: number;
    };
  } | null;
}

function CheckoutStep({
  cartItems,
  total,
  name,
  email,
  onPaymentMethod,
  isProcessing,
  error,
  setError,
  setIsProcessing,
  checkoutSettings,
}: CheckoutStepProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [prVersion, setPrVersion] = useState(0);
  const [showTip, setShowTip] = useState(false);
  const [selectedTipAmount, setSelectedTipAmount] = useState<number | null>(null);
  const [customTipAmount, setCustomTipAmount] = useState<string>("");

  const getTipAmount = () => {
    if (!showTip) return 0;
    if (selectedTipAmount !== null) return selectedTipAmount;
    const customAmount = parseFloat(customTipAmount);
    return isNaN(customAmount) || customAmount < 0 ? 0 : customAmount;
  };

  const finalTotal = total + getTipAmount();

  useEffect(() => {
    if (stripe && total > 0) {
      const tipAmount = getTipAmount();
      const pr = stripe.paymentRequest({
        country: "US",
        currency: "usd",
        total: {
          label: "Order Total",
          amount: Math.round((total + tipAmount) * 100),
        },
        requestPayerEmail: true,
        requestPayerName: true,
      });

      pr.canMakePayment().then((result) => {
        if (result) {
          setPaymentRequest(pr);
          setPrVersion((prev) => prev + 1);
        } else {
          setPaymentRequest(null);
        }
      });

      pr.on("paymentmethod", async (ev: any) => {
        setIsProcessing(true);
        setError(null);
        try {
          await onPaymentMethod(ev.paymentMethod.id, tipAmount > 0 ? tipAmount : undefined);
          ev.complete("success");
        } catch (err) {
          ev.complete("fail");
          setError(err instanceof Error ? err.message : "An error occurred");
          setIsProcessing(false);
        }
      });
    }
  }, [stripe, total, name, email, onPaymentMethod, setIsProcessing, setError, showTip, selectedTipAmount, customTipAmount]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setError("Card element not found");
      setIsProcessing(false);
      return;
    }

    try {
      const result = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name,
          email,
        },
      });

      if (result.error) {
        setError(result.error.message || "An error occurred");
        setIsProcessing(false);
        return;
      }

      const tipAmount = getTipAmount();
      await onPaymentMethod(result.paymentMethod.id, tipAmount > 0 ? tipAmount : undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setIsProcessing(false);
    }
  };

  const [orderSummaryExpanded, setOrderSummaryExpanded] = useState(false);

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="-mx-6 my-4 border-y border-gray-200 bg-gray-100">
          <button
            type="button"
            onClick={() => setOrderSummaryExpanded((prev) => !prev)}
            className="w-full flex items-center justify-between py-4 px-6 text-left"
          >
            <span className="flex items-center gap-1 text-blue-600">
              Order summary
              {orderSummaryExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </span>
            <span className="font-bold text-black">${finalTotal.toFixed(2)}</span>
          </button>
          {orderSummaryExpanded && (
            <div className="py-4 px-6 space-y-2">
              {cartItems.map((item) => (
                <div key={item.product._id} className="flex justify-between text-sm">
                  <span className="text-black">
                    {item.product.name || `Product ${item.product._id}`} × {item.quantity}
                  </span>
                  <span className="text-black">
                    ${((item.product.price || 0) * item.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
              {showTip && getTipAmount() > 0 && (
                <div className="flex justify-between text-sm pt-2">
                  <span className="text-black">Tip</span>
                  <span className="text-black">${getTipAmount().toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold pt-2 border-t border-gray-300">
                <span className="text-black">Total</span>
                <span className="text-black">${finalTotal.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>

        {checkoutSettings?.tipping?.enabled && (
          <div className="py-4 space-y-4 border-t border-gray-300">
            <h3 className="text-lg font-semibold text-black">Add tip</h3>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="show-tip"
                checked={showTip}
                onCheckedChange={(checked) => {
                  setShowTip(checked === true);
                  if (checked) {
                    // Default to first preset amount when checkbox is first selected
                    if (checkoutSettings.tipping.amount1 !== undefined) {
                      setSelectedTipAmount(checkoutSettings.tipping.amount1);
                    }
                    setCustomTipAmount("");
                  } else {
                    setSelectedTipAmount(null);
                    setCustomTipAmount("");
                  }
                }}
              />
              <Label htmlFor="show-tip" className="text-black cursor-pointer">
                Show your support with a tip
              </Label>
            </div>
            {showTip && (
              <div className="space-y-3 pl-6">
                <div className="flex gap-2 flex-wrap">
                  {checkoutSettings.tipping.amount1 !== undefined && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setSelectedTipAmount(checkoutSettings.tipping.amount1!);
                        setCustomTipAmount("");
                      }}
                      className={selectedTipAmount === checkoutSettings.tipping.amount1 ? "bg-gray-500 text-white hover:bg-gray-600" : "text-black"}
                    >
                      ${checkoutSettings.tipping.amount1.toFixed(2)}
                    </Button>
                  )}
                  {checkoutSettings.tipping.amount2 !== undefined && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setSelectedTipAmount(checkoutSettings.tipping.amount2!);
                        setCustomTipAmount("");
                      }}
                      className={selectedTipAmount === checkoutSettings.tipping.amount2 ? "bg-gray-500 text-white hover:bg-gray-600" : "text-black"}
                    >
                      ${checkoutSettings.tipping.amount2.toFixed(2)}
                    </Button>
                  )}
                  {checkoutSettings.tipping.amount3 !== undefined && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setSelectedTipAmount(checkoutSettings.tipping.amount3!);
                        setCustomTipAmount("");
                      }}
                      className={selectedTipAmount === checkoutSettings.tipping.amount3 ? "bg-gray-500 text-white hover:bg-gray-600" : "text-black"}
                    >
                      ${checkoutSettings.tipping.amount3.toFixed(2)}
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="custom-tip" className="text-black">Custom tip</Label>
                  <Input
                    id="custom-tip"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={customTipAmount}
                    onChange={(e) => {
                      setCustomTipAmount(e.target.value);
                      setSelectedTipAmount(null);
                    }}
                    className="text-black"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="py-4 space-y-4">
          {paymentRequest && (
            <>
              <div className="relative bg-white rounded" style={{ height: "48px" }}>
                <PaymentRequestButtonElement
                  key={prVersion}
                  options={{
                    paymentRequest,
                    style: {
                      paymentRequestButton: {
                        type: "default",
                        theme: "light",
                        height: "48px",
                      },
                    },
                  }}
                />
              </div>

              <div className="w-full border-b border-gray-500 my-6 relative">
                <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 bg-white text-gray-500 px-4 text-sm">
                  Or pay with card
                </div>
              </div>
            </>
          )}
          <div className="space-y-2">
            <div className="p-3 border border-gray-300 rounded-md">
              <CardElement options={cardElementOptions} />
            </div>
          </div>
          {error && (
            <p className="text-sm text-red-500 mt-2">{error}</p>
          )}
        </div>

        <button
          type="submit"
          className="w-full bg-black text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!stripe || isProcessing || total === 0}
        >
          {isProcessing ? "Processing..." : "Place Order"}
        </button>
      </form>
    </>
  );
}

export function CheckoutDialog({ open, onOpenChange }: CheckoutDialogProps) {
  const { 
    cart, 
    shippingAddress: contextShippingAddress, 
    customerEmail: contextCustomerEmail,
    setShippingAddress: setContextShippingAddress,
    setCustomerEmail: setContextCustomerEmail,
    updateQuantity: updateCartQuantity,
  } = useCart();
  const { page, stripe, checkoutSettings } = usePage();
  const [step, setStep] = useState(1);
  const [products, setProducts] = useState<Product[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [customerEmail, setCustomerEmail] = useState(contextCustomerEmail);
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress | null>(contextShippingAddress);
  const [isAddressComplete, setIsAddressComplete] = useState(!!contextShippingAddress);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create Stripe promise from publishableKey and stripeAccount
  const stripePromise = useMemo(() => {
    if (!stripe?.publishableKey) {
      return null;
    }
    const options: { stripeAccount?: string } = {};
    if (stripe.accountId) {
      options.stripeAccount = stripe.accountId;
    }
    return loadStripe(stripe.publishableKey, options);
  }, [stripe]);

  // Sync local state with context when context changes
  useEffect(() => {
    setCustomerEmail(contextCustomerEmail);
  }, [contextCustomerEmail]);

  useEffect(() => {
    setShippingAddress(contextShippingAddress);
    setIsAddressComplete(!!contextShippingAddress);
  }, [contextShippingAddress]);

  // Sync quantities from cart when cart changes (e.g., when navigating back)
  useEffect(() => {
    if (products.length > 0 && cart.products.length > 0) {
      const updatedQuantities: Record<string, number> = {};
      cart.products.forEach((item) => {
        updatedQuantities[item.productId] = item.quantity;
      });
      setQuantities(updatedQuantities);
    }
  }, [cart.products, products.length]);

  useEffect(() => {
    if (open && cart.products.length > 0) {
      // Initialize from context when dialog opens
      setCustomerEmail(contextCustomerEmail);
      setShippingAddress(contextShippingAddress);
      setIsAddressComplete(!!contextShippingAddress);
      
      // Fetch product details
      const fetchProducts = async () => {
        try {
          const productPromises = cart.products.map(async (item) => {
            const response = await fetch(`/api/products/${item.productId}`);
            if (response.ok) {
              const data = await response.json();
              return data.product;
            }
            return null;
          });
          const fetchedProducts = await Promise.all(productPromises);
          const validProducts = fetchedProducts.filter((p) => p !== null);
          setProducts(validProducts);
          
          // Initialize quantities from cart
          const initialQuantities: Record<string, number> = {};
          cart.products.forEach((item) => {
            initialQuantities[item.productId] = item.quantity;
          });
          setQuantities(initialQuantities);
        } catch (error) {
          console.error("Error fetching products:", error);
        }
      };
      fetchProducts();
    }
  }, [open, cart.products, contextCustomerEmail, contextShippingAddress]);

  const handleQuantityChange = (productId: string, quantity: number) => {
    setQuantities((prev) => ({
      ...prev,
      [productId]: quantity,
    }));
    // Sync with cart context for persistence
    updateCartQuantity(productId, quantity);
  };

  const handleNext = () => {
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      setStep(3);
    }
  };

  const handlePaymentMethod = async (paymentMethodId: string, tipAmount?: number) => {
    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          paymentMethodId,
          products: getCartItems().map((item) => ({
            productId: item.product._id,
            quantity: item.quantity,
          })),
          email: customerEmail,
          shipping: shippingAddress,
          total: getTotal(),
          tipAmount: tipAmount,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Payment failed");
      }

      const data = await response.json();
      
      // Success - close dialog and reset
      handleClose();
      // Show success message
      alert("Order placed successfully!");
    } catch (err) {
      throw err; // Re-throw to be handled by CheckoutStep
    }
  };

  const handleClose = () => {
    setStep(1);
    setIsProcessing(false);
    setError(null);
    // Don't clear shipping address and email from context - keep them for next checkout
    onOpenChange(false);
  };

  const canProceedToNextStep = () => {
    if (step === 1) {
      return products.length > 0 && Object.keys(quantities).length > 0;
    }
    if (step === 2) {
      return (
        customerEmail.trim() !== "" &&
        isAddressComplete &&
        shippingAddress !== null
      );
    }
    return false;
  };

  const getCartItems = () => {
    return products
      .map((product) => ({
        product,
        quantity: quantities[product._id] || 0,
      }))
      .filter((item) => item.quantity > 0);
  };

  const getTotal = () => {
    return getCartItems().reduce(
      (sum, item) => sum + (item.product.price || 0) * item.quantity,
      0
    );
  };

  const getCustomerName = () => {
    if (shippingAddress?.name) {
      return shippingAddress.name;
    }
    return "";
  };

  const getAddressDefaultValues = () => {
    if (!shippingAddress) {
      return undefined;
    }

    return {
      name: shippingAddress.name || "",
      address: {
        line1: shippingAddress.line1 || "",
        line2: shippingAddress.line2 || "",
        city: shippingAddress.city || "",
        state: shippingAddress.state || "",
        postal_code: shippingAddress.postal_code || "",
        country: shippingAddress.country || "US",
      },
    };
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-none w-screen h-screen max-h-screen translate-x-0 translate-y-0 left-0 top-0 rounded-none overflow-y-auto">
        <div className="max-w-4xl w-full mx-auto h-full flex flex-col">
          <DialogHeader>
            <DialogTitle>{page?.artistName || ""}</DialogTitle>
            <div className="flex items-center justify-center gap-0 py-4">
              {[1, 2, 3].map((s, i) => (
                <div key={s} className="flex items-center">
                  {i > 0 && <div className="h-px w-8 shrink-0 bg-gray-300 mx-1" />}
                  <button
                    type="button"
                    onClick={() => {
                      if (!isProcessing && s < step) {
                        if (s === 1) setStep(1);
                        else if (s === 2) setStep(2);
                      }
                    }}
                    disabled={isProcessing || s > step}
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium transition-colors ${
                      s === step
                        ? "bg-black text-white"
                        : s < step
                          ? "border border-gray-300 bg-white text-black hover:bg-gray-50 cursor-pointer"
                          : "border border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    {s}
                  </button>
                </div>
              ))}
            </div>
          </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-4">
            {products.map((product) => (
              <div
                key={product._id}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div className="flex-1">
                  <div className="font-medium">
                    {product.name || `Product ${product._id}`}
                  </div>
                  <div className="text-sm text-gray-500">
                    ${product.price?.toFixed(2) || "0.00"}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 border rounded-md">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const currentQty = quantities[product._id] || 0;
                        if (currentQty > 0) {
                          handleQuantityChange(product._id, currentQty - 1);
                        }
                      }}
                      disabled={(quantities[product._id] || 0) === 0}
                      className="h-8 w-8"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="min-w-[2rem] text-center font-medium">
                      {quantities[product._id] || 0}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const currentQty = quantities[product._id] || 0;
                        if (currentQty < 10) {
                          handleQuantityChange(product._id, currentQty + 1);
                        }
                      }}
                      disabled={(quantities[product._id] || 0) >= 10}
                      className="h-8 w-8"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {products.length === 0 && (
              <div className="text-center text-gray-500 py-4">
                No products in cart
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="customer-email">Email *</Label>
              <Input
                id="customer-email"
                type="email"
                placeholder="Enter your email"
                value={customerEmail}
                onChange={(e) => {
                  const email = e.target.value;
                  setCustomerEmail(email);
                  setContextCustomerEmail(email);
                }}
              />
            </div>

            <div className="space-y-4">
              <Elements stripe={stripePromise} key={`address-element-${step}-${shippingAddress ? 'has-address' : 'no-address'}`}>
                <AddressElement
                  options={{
                    mode: 'shipping',
                    fields: {
                      phone: 'always',
                    },
                    validation: {
                      phone: {
                        required: 'never',
                      },
                    },
                    defaultValues: getAddressDefaultValues(),
                  }}
                  onChange={(event) => {
                    if (event.complete) {
                      const address = event.value.address as unknown as ShippingAddress;
                      // Extract name if available
                      if (event.value.name) {
                        address.name = event.value.name;
                      }
                      setShippingAddress(address);
                      setContextShippingAddress(address);
                      setIsAddressComplete(true);
                    } else {
                      setIsAddressComplete(false);
                      setContextShippingAddress(null);
                    }
                  }}
                />
              </Elements>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 py-4">
            <Elements stripe={stripePromise}>
              <CheckoutStep
                cartItems={getCartItems()}
                total={getTotal()}
                name={getCustomerName()}
                email={customerEmail}
                onPaymentMethod={handlePaymentMethod}
                isProcessing={isProcessing}
                error={error}
                setError={setError}
                setIsProcessing={setIsProcessing}
                checkoutSettings={checkoutSettings}
              />
            </Elements>
          </div>
        )}

        {step < 3 && (
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              onClick={handleNext}
              disabled={!canProceedToNextStep()}
            >
              Continue
            </Button>
          </div>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
